import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import {
  Container,
  Grid,
  Header,
  Icon,
  Image,
  Menu,
  Segment
} from 'semantic-ui-react'
import { RouteComponentProps } from 'react-router-dom'
// Local
import {
  API_URL,
  ERROR,
  shouldRenderReskin2025
} from '../../constant'
import { api } from '../../action/api'
import UserRegistrationForm from './userRegistrationForm'
import If from '../common/if'
// Styling
import './index.css'
import ReveleerHeaderLogo from '../logos/ReveleerHeaderLogo'
import ReveleerLogoStacked from '../logos/ReveleerLogoStacked'
import { UnregisteredUser } from './unregisteredUser'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>
const SafeHeader = Header as unknown as ComponentType<any>
const SafeImage = Image as unknown as ComponentType<any>

type UserRegistrationSuccess = UnregisteredUser

type UserRegistrationProps = RouteComponentProps<{
  token: string
}>

/**
 * Provides a form for new user registration. Component properties should
 * include a path parameter that is a user registration token. Attempts to
 * fetch the unregistered user data with the token
 * @param token Path parameter containing a user registration token
 */
function UserRegistration(props: UserRegistrationProps): JSX.Element {
  const { match } = props

  const [
    unregisteredUser,
    setUnregisteredUser
  ] = useState<UnregisteredUser | null>(null)
  const [
    errors,
    setErrors
  ] = useState<string[]>([])
  const [
    success,
    setSuccess
  ] = useState<UserRegistrationSuccess | null>(null)

  const fetchUnregisteredUser = (token: string) => {
    const url = API_URL.UNREGISTERED_USER(token)
    api.get(url).then(
      ({ data }: { data: UnregisteredUser }) => {
        setUnregisteredUser(data)
        setErrors([])
        setSuccess(null)
      }
    ).catch(() => {
      setUnregisteredUser(null)
      setErrors([ ERROR.TYPE.EXPIRED_TOKEN ])
      setSuccess(null)
    })
  }

  useEffect(() => {
    if (match && match.params.token) {
      const { token } = match.params
      fetchUnregisteredUser(token)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ match.params.token ])

  const handleSaveUserRegister = (user: UnregisteredUser) => {
    const { token } = match.params
    const url = API_URL.UNREGISTERED_USER(token)
    api.post(
      url,
      user
    ).then(({ data }: { data: UserRegistrationSuccess }) => {
      setErrors([])
      setSuccess(data)
    })
  }

  const renderSuccess = (user: UserRegistrationSuccess): JSX.Element => (
    <SafeGrid
      centered
      columns={ 1 }
    >
      <Grid.Row>
        <Grid.Column
          mobile={ 16 }
          tablet={ 16 }
          computer={ 16 }
        >
          <center>
            <a href="https://curationhealthcare.com/">
              <If isTrue={ !shouldRenderReskin2025 }>
                <SafeImage src="/asset/image/curation-logo-caption.png" />
              </If>
              <If isTrue={ shouldRenderReskin2025 }>
                <ReveleerLogoStacked />
              </If>
            </a>
          </center>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column
          mobile={ 16 }
          tablet={ 16 }
          computer={ 16 }
        >
          <center>
            <span>
              <a href="/">Platform Login</a>
            </span>
            <span> | </span>
            <span>
              <a href="https://curationhealthcare.com/">Company Website</a>
            </span>
          </center>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column
          mobile={ 16 }
          tablet={ 16 }
          computer={ 16 }
        >
          <div className="m-1-top">
            <h1>Thank you!</h1>
          </div>
          <div className="m-1-top">
            Your user registration is complete.
          </div>
          <div className="m-1-top">
            <span>You can sign into</span>
            <span>
              { ' ' }
              <a href="/">the platform</a>
            </span>
            <span> using either your</span>
            <span>
              { ' ' }
              username: <strong>{ user.username }</strong>
            </span>
            <span> or</span>
            <span>
              { ' ' }
              email address: <strong>{ user.email }</strong>
            </span>
          </div>
        </Grid.Column>
      </Grid.Row>
    </SafeGrid>
  )

  const renderForm = (): JSX.Element => (
    <div>
      <SafeHeader
        as="h4"
        attached="top"
        block
        className="main-schedule-header"
      >
        <Icon name="users" /> New User Registration
      </SafeHeader>
      <SafeSegment
        attached
        className="no-margin"
      >
        <SafeGrid>
          <Grid.Column
            mobile={ 16 }
            tablet={ 16 }
            computer={ 16 }
          >
            <UserRegistrationForm
              user={ unregisteredUser }
              token={ match.params.token }
              onSave={ handleSaveUserRegister }
            />
          </Grid.Column>
        </SafeGrid>
      </SafeSegment>
    </div>
  )

  const renderEmpty = (): JSX.Element => (
    <SafeGrid
      centered
      columns={ 1 }
    >
      <Grid.Row>
        <Grid.Column
          mobile={ 16 }
          tablet={ 16 }
          computer={ 16 }
        >
          <center>
            <a href="https://curationhealthcare.com/">
              <If isTrue={ !shouldRenderReskin2025 }>
                <SafeImage src="/asset/image/curation-logo-caption.png" />
              </If>
              <If isTrue={ shouldRenderReskin2025 }>
                <ReveleerLogoStacked />
              </If>
            </a>
          </center>
        </Grid.Column>
      </Grid.Row>
      <If isTrue={ errors && errors.includes(ERROR.TYPE.EXPIRED_TOKEN) }>
        <Grid.Row>
          <Grid.Column
            mobile={ 16 }
            tablet={ 16 }
            computer={ 16 }
          >
            <center>
              <strong>
                <h3>Your registration token expired.</h3>
              </strong>
              <p>
                Please contact CurationHealth Client Success to receive a new registration token.
              </p>
            </center>
          </Grid.Column>
        </Grid.Row>
      </If>
      <Grid.Row>
        <Grid.Column
          mobile={ 16 }
          tablet={ 16 }
          computer={ 16 }
        >
          <center>
            <span>
              <a href="/">Platform Login</a>
            </span>
            <span> | </span>
            <span>
              <a href="https://curationhealthcare.com/">Company Website</a>
            </span>
          </center>
        </Grid.Column>
      </Grid.Row>
    </SafeGrid>
  )

  let content: JSX.Element | null = null
  if (success) {
    content = renderSuccess(success)
  } else if (unregisteredUser) {
    content = renderForm()
  } else {
    content = renderEmpty()
  }

  return (
    <div>
      <Menu borderless>
        <Menu.Item>
          <If isTrue={ !shouldRenderReskin2025 }>
            <SafeImage
              size="small"
              src="/asset/image/curation-logo-no-tag.png"
            />
          </If>
          <If isTrue={ shouldRenderReskin2025 }>
            <ReveleerHeaderLogo />
          </If>
        </Menu.Item>
      </Menu>
      <div className="header-padding">
        <Container>
          { content }
        </Container>
      </div>
    </div>
  )
}

export default UserRegistration
